import React, { useState, useEffect } from "react";

import { supabase } from "../../supabaseClient";
import { useAuth } from "../../context/AuthContext";
import { FaEdit } from "react-icons/fa";
import {
  PrimaryButton,
  SecondaryButton,
  SkeletonRows,
  formatSquadInfo,
} from "../UI/DesignStandards";

import { AlertBox } from "../UI/DesignStandards";
import { ToggleSwitch } from "../UI/DesignStandards";
import {
  AVERAGE_VERIFICATION_EVERYONE,
  AVERAGE_VERIFICATION_OFF,
  AVERAGE_VERIFICATION_OUT_OF_TOWN,
  MAX_AVERAGE_VERIFICATION_MESSAGE,
  averageVerificationInstructionsRequired,
  fetchTournamentSignupSettings,
  getDefaultSignupSettings,
  normalizeAverageVerification,
  notifySignupSettingsChanged,
  onlineSignupsEnabled,
} from "../../utility/signupSettings";
import {
  DEFAULT_BOWLERS_PER_LANE,
  DEFAULT_LANES_USED,
  MAX_BOWLERS_PER_LANE,
  MIN_BOWLERS_PER_LANE,
  formatSignupCapacityPreview,
  signupCapacityPreview,
} from "../../utility/signupOccupancy";
import { decorateSquadEventNames } from "../../utility/squadEventNames";
import { useTournamentId } from "../../context/TournamentRouteContext";

const SettingsGroup = ({ title, note, children }) => (
  <section className="space-y-4">
    <div>
      <h3 className="text-sm font-semibold text-brand-charcoal">{title}</h3>
      {note ? <p className="text-xs text-brand-muted mt-1">{note}</p> : null}
    </div>
    {children}
  </section>
);

const SettingsToggleRow = ({
  label,
  note,
  checked,
  onChange,
  disabled = false,
}) => (
  <div className="flex items-start gap-3">
    <ToggleSwitch
      checked={checked}
      onChange={onChange}
      disabled={disabled}
      className="mt-0.5 shrink-0"
    />
    <div>
      <div
        className={`text-sm font-medium ${
          disabled ? "text-brand-muted" : "text-brand-charcoal"
        }`}
      >
        {label}
      </div>
      {note ? (
        <p className="text-xs text-brand-muted mt-1">{note}</p>
      ) : null}
    </div>
  </div>
);

const SQUAD_SELECT = `
  id,
  squadDate,
  squadTime,
  lanesUsed,
  signupLocked,
  bowlingCenters ( centerName )
`;

const SQUAD_SELECT_WITHOUT_LOCK = `
  id,
  squadDate,
  squadTime,
  lanesUsed,
  bowlingCenters ( centerName )
`;

const SQUAD_SELECT_MIN = `
  id,
  squadDate,
  squadTime,
  bowlingCenters ( centerName )
`;

const fetchTournamentSquads = async (tournamentId) => {
  const query = (select) =>
    supabase
      .from("squads")
      .select(select)
      .eq("tournamentID", tournamentId)
      .eq("isDeleted", false)
      .order("squadDate", { ascending: true })
      .order("squadTime", { ascending: true });

  const withLock = await query(SQUAD_SELECT);
  if (!withLock.error) {
    return decorateSquadEventNames(withLock.data || []);
  }

  const withLanes = await query(SQUAD_SELECT_WITHOUT_LOCK);
  if (!withLanes.error) {
    return decorateSquadEventNames(
      (withLanes.data || []).map((squad) => ({
        ...squad,
        signupLocked: false,
      }))
    );
  }

  const withoutExtras = await query(SQUAD_SELECT_MIN);
  if (!withoutExtras.error) {
    return decorateSquadEventNames(
      (withoutExtras.data || []).map((squad) => ({
        ...squad,
        lanesUsed: null,
        signupLocked: false,
      }))
    );
  }
  throw withLock.error;
};

const SignupSettings = ({ eventsRevision = 0, bracketsRevision = 0 }) => {
  const tournamentId = useTournamentId();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  const [events, setEvents] = useState([]);
  const [squads, setSquads] = useState([]);
  const [settings, setSettings] = useState(() => getDefaultSignupSettings());
  const [originalSettings, setOriginalSettings] = useState(() =>
    getDefaultSignupSettings()
  );
  const [originalSquads, setOriginalSquads] = useState([]);
  const [customizeLanesBySquad, setCustomizeLanesBySquad] = useState(false);
  const [originalCustomizeLanesBySquad, setOriginalCustomizeLanesBySquad] =
    useState(false);
  const [hasBrackets, setHasBrackets] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchData(eventsRevision > 0 || bracketsRevision > 0);
  }, [tournamentId, eventsRevision, bracketsRevision]);

  const fetchData = async (silent = false) => {
    try {
      if (!silent) {
        setLoading(true);
        setError(null);
      }

      // Fetch events for this tournament
      const { data: eventsData, error: eventsError } = await supabase
        .from("tournamentEvents")
        .select("*")
        .eq("tournamentID", tournamentId)
        .eq("isPrimary", true)
        .eq("isDeleted", false);

      if (eventsError) throw eventsError;
      setEvents(eventsData || []);

      const nextSquads = await fetchTournamentSquads(tournamentId);
      setSquads(nextSquads);
      setOriginalSquads(JSON.parse(JSON.stringify(nextSquads)));
      const customize = nextSquads.some((squad) => squad.lanesUsed != null);
      setCustomizeLanesBySquad(customize);
      setOriginalCustomizeLanesBySquad(customize);

      const [{ count: bracketCount }, tournamentSettings] = await Promise.all([
        supabase
          .from("bracketProduct")
          .select("id", { count: "exact", head: true })
          .eq("tournamentID", tournamentId)
          .eq("isDeleted", false),
        fetchTournamentSignupSettings(tournamentId),
      ]);
      setHasBrackets((bracketCount || 0) > 0);

      setSettings(tournamentSettings);
      setOriginalSettings(JSON.parse(JSON.stringify(tournamentSettings))); // Deep copy for original state
    } catch (error) {
      console.error("Error fetching data:", error);
      setError("Failed to load signup settings. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleSettingChange = (field, value) => {
    setSettings((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleToggleChange = (field) => {
    setSettings((prev) => {
      const currentlyOn =
        field === "allowSignups"
          ? onlineSignupsEnabled(prev)
          : Boolean(prev[field]);
      return {
        ...prev,
        [field]: !currentlyOn,
      };
    });
  };

  const handleSquadLanesChange = (squadId, value) => {
    setSquads((prev) =>
      prev.map((squad) =>
        squad.id === squadId ? { ...squad, lanesUsed: value } : squad
      )
    );
  };

  const handleSquadLockChange = (squadId) => {
    setSquads((prev) =>
      prev.map((squad) =>
        squad.id === squadId
          ? { ...squad, signupLocked: !squad.signupLocked }
          : squad
      )
    );
  };

  const handleEditClick = () => {
    setIsEditing(true);
    setError(null);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setSettings(JSON.parse(JSON.stringify(originalSettings)));
    setSquads(JSON.parse(JSON.stringify(originalSquads)));
    setCustomizeLanesBySquad(originalCustomizeLanesBySquad);
    setError(null);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);

      const lanesUsed = settings.lanesUsed === "" || settings.lanesUsed == null
        ? DEFAULT_LANES_USED
        : Number(settings.lanesUsed);
      const bowlersPerLane = Number(settings.bowlersPerLane) || DEFAULT_BOWLERS_PER_LANE;
      const signupsOn = onlineSignupsEnabled(settings);

      if (signupsOn && (!lanesUsed || lanesUsed < 1)) {
        setError("Set max lanes for this tournament.");
        setSaving(false);
        return;
      }
      if (
        bowlersPerLane < MIN_BOWLERS_PER_LANE ||
        bowlersPerLane > MAX_BOWLERS_PER_LANE
      ) {
        setError(
          `Max bowlers per lane must be ${MIN_BOWLERS_PER_LANE} to ${MAX_BOWLERS_PER_LANE}.`
        );
        setSaving(false);
        return;
      }

      if (
        averageVerificationInstructionsRequired({
          averageVerification: settings.averageVerification,
        }) &&
        !String(settings.averageVerificationMessage || "").trim()
      ) {
        setError(
          "Write which average to use. Bowlers see this on signup."
        );
        setSaving(false);
        return;
      }

      const squadUpdates = squads.map((squad) => {
        const raw = squad.lanesUsed;
        const lanes =
          !customizeLanesBySquad ||
          raw === "" ||
          raw == null ||
          raw === undefined
            ? null
            : Number(raw);
        return {
          id: squad.id,
          lanesUsed: lanes,
          signupLocked: Boolean(squad.signupLocked),
        };
      });

      if (customizeLanesBySquad) {
        const bad = squadUpdates.some(
          (row) => row.lanesUsed != null && row.lanesUsed < 1
        );
        if (bad) {
          setError("Each squad override must be at least 1 lane, or blank to use the tournament max.");
          setSaving(false);
          return;
        }
      }

      const maxBowlers =
        (lanesUsed || 1) * bowlersPerLane;

      // Prepare settings for save
      const settingsToSave = {
        tournamentID: tournamentId,
        allowSignups: signupsOn,
        lanesUsed,
        bowlersPerLane,
        maxPerSquad: maxBowlers,
        requireEmail: settings.requireEmail || false,
        requirePhone: settings.requirePhone || false,
        requireUSBCID: settings.requireUSBCID !== false,
        requireAverage: settings.requireAverage !== false,
        averageVerification: normalizeAverageVerification(
          settings.averageVerification
        ),
        averageVerificationMessage: (() => {
          const mode = normalizeAverageVerification(
            settings.averageVerification
          );
          if (mode === AVERAGE_VERIFICATION_OFF) return null;
          const written = String(
            settings.averageVerificationMessage || ""
          ).trim();
          return written
            ? written.slice(0, MAX_AVERAGE_VERIFICATION_MESSAGE)
            : "";
        })(),
        allowOnlineBrackets:
          hasBrackets && (settings.allowOnlineBrackets || false),
        waitlistEnabled: settings.waitlistEnabled !== false,
        paymentRequiredToHold: Boolean(settings.paymentRequiredToHold),
        closeAllHoursBeforeFirstSquad:
          settings.closeAllHoursBeforeFirstSquad ?? null,
        closeHoursBeforeEachSquad:
          settings.closeAllHoursBeforeFirstSquad == null
            ? settings.closeHoursBeforeEachSquad === 0 ||
              settings.closeHoursBeforeEachSquad === "0"
              ? 0
              : Number(settings.closeHoursBeforeEachSquad) || 48
            : null,
        createdBy: user.id,
        updatedBy: user.id,
      };

      // Check if settings exist for this tournament
      const { data: existingSettings, error: fetchError } = await supabase
        .from("tournamentSignupSettings")
        .select("id")
        .eq("tournamentID", tournamentId);

      let error = null;

      if (fetchError || !existingSettings || existingSettings.length === 0) {
        // No existing settings, insert new
        const { error: insertError } = await supabase
          .from("tournamentSignupSettings")
          .insert(settingsToSave);
        if (insertError) error = insertError;
      } else {
        // Existing settings found, update
        const { error: updateError } = await supabase
          .from("tournamentSignupSettings")
          .update({
            allowSignups: settingsToSave.allowSignups,
            lanesUsed: settingsToSave.lanesUsed,
            bowlersPerLane: settingsToSave.bowlersPerLane,
            maxPerSquad: settingsToSave.maxPerSquad,
            requireEmail: settingsToSave.requireEmail,
            requirePhone: settingsToSave.requirePhone,
            requireUSBCID: settingsToSave.requireUSBCID,
            requireAverage: settingsToSave.requireAverage,
            averageVerification: settingsToSave.averageVerification,
            averageVerificationMessage:
              settingsToSave.averageVerificationMessage,
            allowOnlineBrackets: settingsToSave.allowOnlineBrackets,
            waitlistEnabled: settingsToSave.waitlistEnabled,
            paymentRequiredToHold: settingsToSave.paymentRequiredToHold,
            closeAllHoursBeforeFirstSquad:
              settingsToSave.closeAllHoursBeforeFirstSquad,
            closeHoursBeforeEachSquad: settingsToSave.closeHoursBeforeEachSquad,
            updatedBy: settingsToSave.updatedBy,
            updatedAt: new Date().toISOString(),
          })
          .eq("tournamentID", tournamentId);

        if (updateError) error = updateError;
      }

      if (error) throw error;

      if (squadUpdates.length) {
        const squadResults = await Promise.all(
          squadUpdates.map((row) =>
            supabase
              .from("squads")
              .update({
                lanesUsed: row.lanesUsed,
                signupLocked: row.signupLocked,
              })
              .eq("id", row.id)
          )
        );
        const squadError = squadResults.find((result) => result.error)?.error;
        if (squadError) {
          const lanesOnly = await Promise.all(
            squadUpdates.map((row) =>
              supabase
                .from("squads")
                .update({ lanesUsed: row.lanesUsed })
                .eq("id", row.id)
            )
          );
          const lanesError = lanesOnly.find((result) => result.error)?.error;
          if (lanesError) throw lanesError;
          if (squadUpdates.some((row) => row.signupLocked)) {
            throw squadError;
          }
        }
      }

      const savedSquads = squads.map((squad) => {
        const saved = squadUpdates.find((row) => row.id === squad.id);
        return saved
          ? {
              ...squad,
              lanesUsed: saved.lanesUsed,
              signupLocked: saved.signupLocked,
            }
          : squad;
      });
      setSquads(savedSquads);
      setOriginalSquads(JSON.parse(JSON.stringify(savedSquads)));
      setCustomizeLanesBySquad(customizeLanesBySquad);
      setOriginalCustomizeLanesBySquad(customizeLanesBySquad);
      setOriginalSettings(
        JSON.parse(
          JSON.stringify({
            ...settings,
            lanesUsed,
            bowlersPerLane,
            maxPerSquad: maxBowlers,
          })
        )
      );
      setSettings((prev) => ({
        ...prev,
        lanesUsed,
        bowlersPerLane,
        maxPerSquad: maxBowlers,
      }));
      setIsEditing(false);
      notifySignupSettingsChanged(tournamentId, settingsToSave);
    } catch (error) {
      console.error("Error saving settings:", error);
      setError("Failed to save settings: " + error.message);
    } finally {
      setSaving(false);
    }
  };

  const averageVerification = normalizeAverageVerification(
    settings.averageVerification
  );
  const capacityPreview = signupCapacityPreview(settings, events);
  const signupsOn = onlineSignupsEnabled(settings);
  const muted = !isEditing || !signupsOn;
  const fieldClass = (disabled) =>
    `px-3 py-1 border rounded-md text-sm ${
      disabled
        ? "bg-brand-cream text-brand-muted cursor-not-allowed"
        : "bg-white border-brand-charcoal/20 focus:ring-2 focus:ring-brand-gold-ink"
    }`;
  const labelClass = (disabled) =>
    `text-sm font-medium ${
      disabled ? "text-brand-muted" : "text-brand-charcoal"
    }`;

  const closeAllOn =
    settings.closeAllHoursBeforeFirstSquad != null &&
    settings.closeAllHoursBeforeFirstSquad !== "";
  const closeEachHours =
    settings.closeHoursBeforeEachSquad === 0 ||
    settings.closeHoursBeforeEachSquad === "0"
      ? 0
      : Number(settings.closeHoursBeforeEachSquad) || 48;

  const squadRowLabel = (squad) => {
    const when = formatSquadInfo(squad.squadDate, squad.squadTime);
    const eventName = (squad.eventNames || []).join(" · ");
    const house = squad.bowlingCenters?.centerName;
    return [when, eventName, house].filter(Boolean).join(" · ");
  };

  if (loading) {
    return (
      <div className="card-board p-6 mb-6">
        <h2 className="heading-display text-xl mb-6">Online Signup Settings</h2>
        <SkeletonRows count={5} />
      </div>
    );
  }

  return (
    <div className="card-board p-6 mb-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="heading-display text-xl">
          Online Signup Settings
        </h2>
        {!isEditing && (
          <SecondaryButton
            onClick={handleEditClick}
            icon={FaEdit}
            className="!px-3 !py-2 text-sm"
          >
            Edit Settings
          </SecondaryButton>
        )}
      </div>

      {error && <AlertBox type="error" title="Error" message={error} />}

      <div className="card-inset p-4 space-y-6">
        <SettingsGroup title="Signup">
          <SettingsToggleRow
            label="Enable online signups"
            checked={signupsOn}
            onChange={() => handleToggleChange("allowSignups")}
            disabled={!isEditing}
          />
          <SettingsToggleRow
            label="Waitlist when the squad is full"
            checked={settings.waitlistEnabled !== false}
            onChange={() => handleToggleChange("waitlistEnabled")}
            disabled={muted}
          />
          <SettingsToggleRow
            label="Payment required to hold the spot"
            note="When Card payments is on, Pay now keeps the spot and Pay at the desk is off. When Card payments is off, Pay at the desk still closes Cart. An unpaid hold ends after 24 hours."
            checked={Boolean(settings.paymentRequiredToHold)}
            onChange={() => handleToggleChange("paymentRequiredToHold")}
            disabled={muted}
          />
          <SettingsToggleRow
            label="Allow bracket tickets on online signup"
            checked={hasBrackets && (settings.allowOnlineBrackets || false)}
            onChange={() => handleToggleChange("allowOnlineBrackets")}
            disabled={muted || !hasBrackets}
          />

          <div className="flex flex-wrap gap-6">
            <div className="w-72 max-w-full">
              <label className={`${labelClass(muted)} block mb-1`}>
                Close all online signup
              </label>
              <select
                value={
                  closeAllOn
                    ? String(settings.closeAllHoursBeforeFirstSquad)
                    : ""
                }
                onChange={(e) => {
                  const closeAll =
                    e.target.value === "" ? null : Number(e.target.value);
                  setSettings((prev) => ({
                    ...prev,
                    closeAllHoursBeforeFirstSquad: closeAll,
                    closeHoursBeforeEachSquad:
                      closeAll == null
                        ? prev.closeHoursBeforeEachSquad == null ||
                          prev.closeHoursBeforeEachSquad === ""
                          ? 48
                          : prev.closeHoursBeforeEachSquad
                        : null,
                  }));
                }}
                disabled={muted}
                className={`w-full ${fieldClass(muted)}`}
              >
                <option value="">Off</option>
                <option value="48">48 hours before the first squad</option>
                <option value="24">24 hours before the first squad</option>
                <option value="0">Start of first squad</option>
              </select>
            </div>

            {closeAllOn ? null : (
              <div className="w-72 max-w-full">
                <label className={`${labelClass(muted)} block mb-1`}>
                  Close each squad
                </label>
                <select
                  value={String(closeEachHours)}
                  onChange={(e) =>
                    handleSettingChange(
                      "closeHoursBeforeEachSquad",
                      Number(e.target.value)
                    )
                  }
                  disabled={muted}
                  className={`w-full ${fieldClass(muted)}`}
                >
                  <option value="48">48 hours before that squad</option>
                  <option value="24">24 hours before that squad</option>
                  <option value="0">Start of squad</option>
                </select>
              </div>
            )}
          </div>

          <div className="space-y-4">
            <h4 className="text-sm font-medium text-brand-charcoal">
              Lock squads
            </h4>
            {squads.length === 0 ? (
              <p className="text-xs text-brand-muted">
                Add squads on Setup, then lock them here.
              </p>
            ) : (
              squads.map((squad) => (
                <SettingsToggleRow
                  key={squad.id}
                  label={squadRowLabel(squad)}
                  checked={Boolean(squad.signupLocked)}
                  onChange={() => handleSquadLockChange(squad.id)}
                  disabled={muted}
                />
              ))
            )}
          </div>
        </SettingsGroup>

        <div className="border-t border-brand-charcoal/10 pt-6">
          <SettingsGroup title="Lanes">
            <div className="flex flex-wrap gap-6">
              <div>
                <label className={`${labelClass(muted)} block mb-1`}>
                  Max lanes
                </label>
                <input
                    type="number"
                    min="1"
                    value={settings.lanesUsed ?? ""}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === "" || parseInt(value, 10) >= 1) {
                        handleSettingChange(
                          "lanesUsed",
                          value === "" ? null : parseInt(value, 10)
                        );
                      }
                    }}
                    disabled={muted}
                    className={`w-24 ${fieldClass(muted)}`}
                  />
              </div>

              <div>
                <label className={`${labelClass(muted)} block mb-1`}>
                  Max bowlers per lane
                </label>
                <input
                    type="number"
                    min={MIN_BOWLERS_PER_LANE}
                    max={MAX_BOWLERS_PER_LANE}
                    value={settings.bowlersPerLane ?? DEFAULT_BOWLERS_PER_LANE}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === "") return;
                      const n = parseInt(value, 10);
                      if (
                        n >= MIN_BOWLERS_PER_LANE &&
                        n <= MAX_BOWLERS_PER_LANE
                      ) {
                        handleSettingChange("bowlersPerLane", n);
                      }
                    }}
                    disabled={muted}
                    className={`w-24 ${fieldClass(muted)}`}
                  />
              </div>
            </div>

            <p className="text-xs text-brand-muted">
              {formatSignupCapacityPreview(capacityPreview)}
            </p>

            <SettingsToggleRow
              label="Set max lanes per squad"
              checked={customizeLanesBySquad}
              onChange={() => setCustomizeLanesBySquad((prev) => !prev)}
              disabled={muted}
            />
            {customizeLanesBySquad ? (
                squads.length === 0 ? (
                  <p className="text-xs text-brand-muted mt-2">
                    Add squads on Setup, then set max lanes here.
                  </p>
                ) : (
                  <div className="mt-3 space-y-2">
                    {squads.map((squad) => (
                      <div
                        key={squad.id}
                        className="flex items-center gap-3"
                      >
                        <label
                          className={`text-xs ${
                            muted ? "text-brand-muted" : "text-brand-charcoal"
                          }`}
                        >
                          {squadRowLabel(squad)}
                        </label>
                        <input
                          type="number"
                          min="1"
                          placeholder={
                            settings.lanesUsed
                              ? String(settings.lanesUsed)
                              : "Max"
                          }
                          value={squad.lanesUsed ?? ""}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (value === "" || parseInt(value, 10) >= 1) {
                              handleSquadLanesChange(
                                squad.id,
                                value === "" ? null : parseInt(value, 10)
                              );
                            }
                          }}
                          disabled={muted}
                          className={`${fieldClass(muted)} w-20`}
                        />
                      </div>
                    ))}
                    <p className="text-xs text-brand-muted">
                      Blank uses the tournament max.
                    </p>
                  </div>
                )
              ) : null}
          </SettingsGroup>
        </div>

        <div className="border-t border-brand-charcoal/10 pt-6">
          <SettingsGroup title="Online signup fields">
            <SettingsToggleRow
              label="Require teammate emails"
              note="The person who registers already uses their account email."
              checked={settings.requireEmail || false}
              onChange={() => handleToggleChange("requireEmail")}
              disabled={muted}
            />
            <SettingsToggleRow
              label="Require Phone"
              checked={settings.requirePhone || false}
              onChange={() => handleToggleChange("requirePhone")}
              disabled={muted}
            />
            <SettingsToggleRow
              label="Require USBC ID"
              checked={settings.requireUSBCID !== false}
              onChange={() => handleToggleChange("requireUSBCID")}
              disabled={muted}
            />
            <SettingsToggleRow
              label="Require Average"
              checked={settings.requireAverage !== false}
              onChange={() => handleToggleChange("requireAverage")}
              disabled={muted}
            />
          </SettingsGroup>
        </div>

        <div className="border-t border-brand-charcoal/10 pt-6 space-y-4">
          <div>
            <label className={`${labelClass(muted)} block mb-1`}>
              Require Average Verification Upload
            </label>
            <select
              value={averageVerification}
              onChange={(e) =>
                handleSettingChange("averageVerification", e.target.value)
              }
              disabled={muted}
              className={`w-72 max-w-full ${fieldClass(muted)}`}
            >
              <option value={AVERAGE_VERIFICATION_OFF}>Off</option>
              <option value={AVERAGE_VERIFICATION_OUT_OF_TOWN}>
                Out-of-town bowlers
              </option>
              <option value={AVERAGE_VERIFICATION_EVERYONE}>Everyone</option>
            </select>
            {averageVerification === AVERAGE_VERIFICATION_EVERYONE ? (
              <p className="text-xs text-brand-muted mt-1">
                Every online signup must upload average verification.
              </p>
            ) : averageVerification === AVERAGE_VERIFICATION_OUT_OF_TOWN ? (
              <p className="text-xs text-brand-muted mt-1">
                Average verification is required for out-of-town bowlers.
              </p>
            ) : null}
          </div>

          {averageVerification !== AVERAGE_VERIFICATION_OFF ? (
            <div>
              <label className={`${labelClass(muted)} block mb-1`}>
                Which average to use
                {isEditing ? " (required)" : ""}
              </label>
              {isEditing ? (
                <>
                  <textarea
                    value={settings.averageVerificationMessage || ""}
                    onChange={(e) =>
                      handleSettingChange(
                        "averageVerificationMessage",
                        e.target.value.slice(0, MAX_AVERAGE_VERIFICATION_MESSAGE)
                      )
                    }
                    required
                    rows={4}
                    placeholder="Example: Highest USBC league average of 21 games as of March 14, 2026. If none, highest 2024-2025 book. Out-of-town bowlers must upload written verification."
                    className="w-full px-3 py-2 border rounded-md text-sm bg-white border-brand-charcoal/20"
                  />
                  <p className="text-xs text-brand-muted mt-1">
                    Bowlers see this labeled as from the tournament director.
                    Say which average, as of what date, and who must upload
                    proof.
                  </p>
                </>
              ) : (
                <p className="text-sm text-brand-charcoal whitespace-pre-wrap">
                  {settings.averageVerificationMessage ||
                    "No instructions saved."}
                </p>
              )}
            </div>
          ) : null}
        </div>
      </div>

      {isEditing && (
        <div className="flex space-x-4 pt-4">
          <PrimaryButton onClick={handleSave} disabled={saving}>
            {saving ? "Saving..." : "Save Changes"}
          </PrimaryButton>
          <SecondaryButton onClick={handleCancel} disabled={saving}>
            Cancel
          </SecondaryButton>
        </div>
      )}
    </div>
  );
};

export default SignupSettings;
